import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Play, FileText, Package, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};

const VideoPlayer = ({ url }) => {
  if (!url) return null;
  const id = ytId(url);
  if (id) return <div className="aspect-video rounded-lg overflow-hidden bg-black"><iframe title="video" className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} allowFullScreen /></div>;
  if (url.match(/\.(mp4|mov|webm)$/i)) return <video src={url} controls className="w-full rounded-lg" />;
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline">Open video <ExternalLink className="w-3 h-3 inline" /></a>;
};

const ProductDetailPage = () => {
  const { productKey } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true); setError(null);
    api.get(`/store/products/${productKey}`)
      .then((r) => setP(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Product not found or no longer available"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [productKey]);

  if (loading) return (
    <div className="max-w-6xl mx-auto p-12 text-center text-slate-500" data-testid="product-loading">
      <div className="inline-block w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-3"></div>
      <div>Loading product…</div>
    </div>
  );

  if (error || !p) return (
    <div className="max-w-xl mx-auto p-10 text-center" data-testid="product-error">
      <div className="w-14 h-14 mx-auto rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3"><Package className="w-7 h-7" /></div>
      <div className="text-lg font-semibold text-slate-800">{error || "Product not available"}</div>
      <p className="mt-1 text-sm text-slate-500">File missing ya product delete ho gaya ho sakta hai.</p>
      <div className="mt-4 flex gap-2 justify-center">
        <Button variant="outline" onClick={load} data-testid="product-retry"><RefreshCw className="w-4 h-4 mr-1" /> Retry</Button>
        <Button onClick={() => navigate("/store")} className="bg-orange-600 hover:bg-orange-700 text-white">Back to Store</Button>
      </div>
    </div>
  );

  return (
    <div data-testid="product-detail-page" className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate("/store")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Store</Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="rounded-xl overflow-hidden bg-slate-100 aspect-video">
            {p.thumbnail_file ? <img src={`${API}/store/products/${p.key}/thumbnail`} alt={p.name} className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center text-slate-400"><Package className="w-10 h-10" /></div>}
          </div>
          {(p.screenshot_files || []).length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {p.screenshot_files.map((s) => (
                <a key={s.file_id} href={`${API}/store/products/${p.key}/screenshots/${s.file_id}`} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-md overflow-hidden bg-slate-100 hover:ring-2 ring-orange-500">
                  <img src={`${API}/store/products/${p.key}/screenshots/${s.file_id}`} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
          {p.demo_video_url && <div className="mt-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Demo Video</div><VideoPlayer url={p.demo_video_url} /></div>}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold">{p.category}</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold text-slate-900">{p.name}</h1>
          <div className="mt-1 flex gap-1 flex-wrap">
            {p.highlight && <Badge className="bg-pink-100 text-pink-800">⭐ Highlight</Badge>}
            {p.featured && <Badge className="bg-orange-100 text-orange-800">Featured</Badge>}
            {p.is_new && <Badge className="bg-green-100 text-green-800">New</Badge>}
            {p.version && <Badge className="bg-slate-100 text-slate-700">v{p.version}</Badge>}
            {p.file_size_mb && <Badge className="bg-slate-100 text-slate-700">{p.file_size_mb} MB</Badge>}
          </div>
          <p className="mt-3 text-slate-700">{p.short_desc}</p>

          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
            {p.has_file ? (
              <>
                <div className="text-sm text-slate-600 mb-3">📥 यह फाइल मुफ्त है — बिना किसी भुगतान के डाउनलोड करें।</div>
                <a
                  href={`${API}/store/download-public/${p.key}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex h-12 items-center justify-center w-full rounded-md bg-green-600 hover:bg-green-700 text-white px-4 font-semibold text-base"
                  data-testid="free-download-btn"
                >
                  <Download className="w-5 h-5 mr-2" /> Download Now (Free)
                </a>
              </>
            ) : (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                ⚠️ File abhi upload nahi hui. Admin ko batayein — jaise hi file available hogi, download button yahaan dikhega.
              </div>
            )}
            {p.system_requirement && <div className="mt-3 text-xs text-slate-500"><b>Requirements:</b> {p.system_requirement}</div>}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <Tabs defaultValue="desc">
          <TabsList>
            <TabsTrigger value="desc">Description</TabsTrigger>
            {(p.sample_files || []).length > 0 && <TabsTrigger value="sample">Preview</TabsTrigger>}
            {p.installation_guide && <TabsTrigger value="install">Installation Guide</TabsTrigger>}
            {p.tutorial_video_url && <TabsTrigger value="tutorial">Tutorial Video</TabsTrigger>}
          </TabsList>
          <TabsContent value="desc">
            <div className="rounded-lg bg-white border border-slate-200 p-6 whitespace-pre-wrap text-slate-800 leading-relaxed">{p.full_desc || p.short_desc || "No description."}</div>
          </TabsContent>
          {(p.sample_files || []).length > 0 && (
            <TabsContent value="sample">
              <div className="rounded-lg bg-white border border-slate-200 p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {p.sample_files.map((s) => (
                    <a key={s.file_id} href={`${API}/store/products/${p.key}/sample/${s.file_id}`} target="_blank" rel="noopener noreferrer" className="rounded-md border border-slate-200 p-3 hover:bg-slate-50 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-700" />
                      <span className="text-sm text-slate-800 truncate">{s.original_filename}</span>
                      <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                    </a>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}
          {p.installation_guide && (
            <TabsContent value="install"><div className="rounded-lg bg-white border border-slate-200 p-6 whitespace-pre-wrap text-slate-800 leading-relaxed">{p.installation_guide}</div></TabsContent>
          )}
          {p.tutorial_video_url && (
            <TabsContent value="tutorial"><div className="rounded-lg bg-white border border-slate-200 p-6"><VideoPlayer url={p.tutorial_video_url} /></div></TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ProductDetailPage;
